import { Button, Css, ModalBody, ModalFooter, ModalHeader, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useState } from "react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { DimensionTypeCheckboxes } from "src/routes/bids/plan-based-bid-package/components/DimensionTypeCheckboxes";
import { PersonalizationGroupsField } from "src/routes/bids/plan-based-bid-package/components/PersonalizationGroupsField";
import {
  buildDimensions,
  buildPersonalizationDimensions,
  nextBaseGroupClientId,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageBidPlanGroupDimension,
  PlanBasedBidPackageFormState,
  renumberGroups,
} from "src/routes/bids/plan-based-bid-package/utils";

type BasePlanGroupDimensionsModalProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  // When set, the modal edits this base group's dimensions; otherwise it creates a new base group on save.
  editingGroup?: ObjectState<PlanBasedBidPackageBidPlanGroup>;
};

export function BasePlanGroupDimensionsModal(props: BasePlanGroupDimensionsModalProps) {
  const { formState, dimensionResults, editingGroup } = props;
  const { closeModal } = useModal();
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>(
    (editingGroup?.dimensions.value ?? []).map((d) => d.globalOptionTypeId).filter((id): id is string => !!id),
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    (editingGroup?.dimensions.value ?? []).map((d) => d.globalOptionGroupId).filter((id): id is string => !!id),
  );

  // Combine the checked type-based and selected group-based dimensions, keeping any prior rows.
  function dimensionsFrom(existing: PlanBasedBidPackageBidPlanGroupDimension[]) {
    return [
      ...buildDimensions(selectedTypeIds, existing, dimensionResults),
      ...buildPersonalizationDimensions(selectedGroupIds, existing, dimensionResults),
    ];
  }

  function save() {
    if (editingGroup) {
      editingGroup.dimensions.set(dimensionsFrom(editingGroup.dimensions.value ?? []));
    } else {
      // Create a new base group (no globalOptionId) with the selected dimensions, then renumber globally.
      const groups = formState.bidPlanGroups.value ?? [];
      const newGroup = { clientId: nextBaseGroupClientId(groups), archive: false, dimensions: dimensionsFrom([]) };
      formState.bidPlanGroups.set(renumberGroups([...groups, newGroup]));
    }
    closeModal();
  }

  return (
    <>
      <ModalHeader>Base Plan Price Dimensions</ModalHeader>
      <ModalBody>
        <div css={Css.mb3.$}>Define the attributes to include as lines in your bid sheet.</div>
        <div css={Css.df.fdc.gap3.$}>
          <DimensionTypeCheckboxes
            dimensionResults={dimensionResults}
            selectedTypeIds={selectedTypeIds}
            onChange={setSelectedTypeIds}
          />
          <PersonalizationGroupsField
            dimensionResults={dimensionResults}
            selectedGroupIds={selectedGroupIds}
            onChange={setSelectedGroupIds}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Save" onClick={save} />
      </ModalFooter>
    </>
  );
}
